<i18n locale="en">
{
  "customFont": "Your custom font",
  "uploadFontBtn": "Upload font",
  "replaceFontBtn": "Replace font",
  "removeFontBtn": "Remove",
  "fontProcessing": "Converting font...",
  "fontFailed": "Font conversion failed. Please try again.",
  "fontUploadError": "Unable to upload font",
  "fontRemoveError": "Unable to remove font",
  "agreementRequired": "Please accept the upload agreement to continue."
}
</i18n>

<template>
  <div class="custom-font-uploader flex-col gap-2">
    <OwnType
      :text="t('customFont')"
      variant="paragraph-small"
      color="secondary"
    />

    <div
      v-if="customFontStatus === 'processing'"
      class="custom-font-uploader__status align-center flex-row gap-2"
    >
      <OwnProgressSpinner />
      <OwnType
        :text="t('fontProcessing')"
        variant="paragraph-small"
        color="secondary"
      />
    </div>

    <div
      v-else-if="customFontStatus === 'ready'"
      class="custom-font-uploader__status align-center flex-row gap-2"
    >
      <OwnType
        :text="customFontName ?? ''"
        variant="paragraph-small"
        color="primary"
      />
      <OwnButton
        :text="t('removeFontBtn')"
        variant="secondary"
        size="sm"
        data-testid="remove-custom-font-btn"
        @click="removeCustomFont"
      />
    </div>

    <div
      v-else-if="customFontStatus === 'failed'"
      class="custom-font-uploader__status"
    >
      <OwnType
        :text="t('fontFailed')"
        variant="paragraph-small"
        color="danger"
      />
    </div>

    <div v-if="customFontStatus !== 'processing'">
      <input
        ref="fontFileInput"
        class="custom-font-uploader__file-input"
        type="file"
        accept=".ttf,.otf,.woff,.woff2"
        data-testid="custom-font-file-input"
        @change="onFontFileSelected"
      />
      <OwnButton
        :text="
          customFontStatus === 'ready'
            ? t('replaceFontBtn')
            : t('uploadFontBtn')
        "
        variant="secondary"
        size="sm"
        data-testid="upload-custom-font-btn"
        @click="onUploadClick"
      />
    </div>

    <CustomFontUploadAgreementModal
      v-if="agreementModalOpen"
      :agreement="agreement"
      :brand-id="activeBrandId"
      @close="onAgreementClose"
      @accepted="onAgreementAccepted"
    />
  </div>
</template>

<script lang="ts" setup>
import { storeToRefs } from 'pinia'
import { onMounted, onUnmounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'

import { useAuthStore } from '@/auth/useAuthStore'
import { useApplicationScope } from '@/core/store'
import { logError } from '@/logger'
import { OlympusClient } from '@/OlympusClient'
import { OwnButton } from '@/ui/OwnButton'
import { OwnProgressSpinner } from '@/ui/OwnProgressSpinner'
import { OwnType } from '@/ui/OwnType'
import { useNotify } from '@/utils/useNotify'

import CustomFontUploadAgreementModal from './CustomFontUploadAgreementModal.vue'

type Agreement = Awaited<
  ReturnType<typeof OlympusClient.legal.v1.agreements.getAgreements>
>['results'][number]

type Acceptance = Awaited<
  ReturnType<typeof OlympusClient.legal.v1.brands.brand.acceptances.list>
>['results'][number]

const fontMimeTypes: Record<string, string> = {
  otf: 'font/otf',
  ttf: 'font/ttf',
  woff: 'font/woff',
  woff2: 'font/woff2',
}

const FONT_POLL_INTERVAL = 3000
const FONT_POLL_MAX_ERRORS = 3
const AGREEMENT_TYPE = 'custom-font-upload'

const { t } = useI18n()
const notify = useNotify()
const { activeBrandId } = useApplicationScope()
const { user } = storeToRefs(useAuthStore())

const customFontStatus = ref<string | null>(null)
const customFontName = ref<string | null>(null)
const fontFileInput = ref<HTMLInputElement | null>(null)
const agreement = ref<Agreement | null>(null)
const hasAccepted = ref(false)
const agreementModalOpen = ref(false)
let fontPollingTimer: ReturnType<typeof setInterval> | null = null
let fontPollErrorCount = 0

const loadCustomFontStatus = async (): Promise<void> => {
  try {
    const theme = await OlympusClient.brandsV0.brand.theme.getBrandTheme({
      params: { brandId: activeBrandId.value },
    })
    const overrides = (
      theme as {
        overrides?: {
          font?: { customFontStatus?: string; customFontName?: string }
        }
      }
    ).overrides
    customFontStatus.value = overrides?.font?.customFontStatus ?? null
    customFontName.value = overrides?.font?.customFontName ?? null

    if (customFontStatus.value === 'processing') {
      startFontPolling()
    }
  } catch {
    // Theme might not have custom font data yet
  }
}

const loadAgreementAndAcceptance = async (): Promise<void> => {
  try {
    const { results } = await OlympusClient.legal.v1.agreements.getAgreements({
      query: { type: AGREEMENT_TYPE },
    })
    if (results.length === 0) return
    agreement.value = results[0] ?? null

    if (!agreement.value || !user.value) return

    const acceptances =
      await OlympusClient.legal.v1.brands.brand.acceptances.list({
        params: { brandId: activeBrandId.value },
        query: { agreementId: agreement.value.id, type: AGREEMENT_TYPE },
      })

    const userId = user.value.id
    hasAccepted.value = acceptances.results.some(
      (a: Acceptance) => a.fingerprint?.signingUserId === userId
    )
  } catch (err) {
    logError(err)
  }
}

const startFontPolling = (): void => {
  stopFontPolling()
  fontPollingTimer = setInterval(async () => {
    try {
      const theme = await OlympusClient.brandsV0.brand.theme.getBrandTheme({
        params: { brandId: activeBrandId.value },
      })
      const overrides = (
        theme as {
          overrides?: {
            font?: { customFontStatus?: string; customFontName?: string }
          }
        }
      ).overrides
      const status = overrides?.font?.customFontStatus ?? null
      customFontStatus.value = status
      customFontName.value = overrides?.font?.customFontName ?? null
      fontPollErrorCount = 0

      if (status !== 'processing') {
        stopFontPolling()
      }
    } catch {
      fontPollErrorCount++
      if (fontPollErrorCount >= FONT_POLL_MAX_ERRORS) {
        stopFontPolling()
        customFontStatus.value = 'failed'
      }
    }
  }, FONT_POLL_INTERVAL)
}

const stopFontPolling = (): void => {
  if (fontPollingTimer) {
    clearInterval(fontPollingTimer)
    fontPollingTimer = null
  }
}

const onUploadClick = (): void => {
  if (!hasAccepted.value) {
    agreementModalOpen.value = true
    return
  }
  fontFileInput.value?.click()
}

const onAgreementClose = (): void => {
  agreementModalOpen.value = false
}

const onAgreementAccepted = (): void => {
  agreementModalOpen.value = false
  hasAccepted.value = true
  fontFileInput.value?.click()
}

const onFontFileSelected = async (event: Event): Promise<void> => {
  const input = event.target as HTMLInputElement
  const file = input.files?.[0]
  if (!file) return
  input.value = ''

  const ext = file.name.split('.').pop()?.toLowerCase() ?? ''
  const contentType = fontMimeTypes[ext]
  if (!contentType) return

  const fontName = file.name.replace(/\.[^.]+$/, '').replace(/[-_]/g, ' ')

  try {
    const { resultUrl, uploadUrl } = await OlympusClient.requestFileUploadKey({
      query: {
        contentType: contentType as 'font/woff2',
        group: 'font-originals',
        suffix: `.${ext}`,
      },
    })

    await fetch(uploadUrl, {
      body: file,
      headers: { 'Content-Type': contentType },
      method: 'PUT',
    })

    const result = await OlympusClient.brandsV0.brand.theme.triggerCustomFont({
      body: { fontName, originalUrl: resultUrl },
      params: { brandId: activeBrandId.value },
    })

    customFontName.value = fontName

    if (result.status === 'processing') {
      customFontStatus.value = 'processing'
      startFontPolling()
    } else if (result.status === 'ready') {
      customFontStatus.value = 'ready'
    }
  } catch (err) {
    if (
      err &&
      OlympusClient.brandsV0.brand.theme.triggerCustomFont.isError(err) &&
      err.code === 'agreement-required'
    ) {
      hasAccepted.value = false
      agreementModalOpen.value = true
      notify(t('agreementRequired'), 'error')
      return
    }
    logError(err)
    notify(t('fontUploadError'), 'error')
  }
}

const removeCustomFont = async (): Promise<void> => {
  try {
    await OlympusClient.brandsV0.brand.theme.deleteCustomFont({
      params: { brandId: activeBrandId.value },
    })
    customFontStatus.value = null
    customFontName.value = null
  } catch (err) {
    logError(err)
    notify(t('fontRemoveError'), 'error')
  }
}

onMounted(() => {
  loadCustomFontStatus()
  loadAgreementAndAcceptance()
})

onUnmounted(stopFontPolling)
</script>

<style lang="scss" scoped>
.custom-font-uploader {
  padding: 12px;
  border-top: 1px solid var(--divider);

  &__file-input {
    display: none;
  }

  &__status {
    min-height: 24px;
  }
}
</style>
